// Fields that are internal bookkeeping, not meaningful to show as a "change"
// in an audit view.
const NOISE_FIELDS = new Set([
  "id",
  "createdAt",
  "updatedAt",
  "docHash",
  "sifra",
  "searchVector",
]);

const isDataUri = (value) =>
  typeof value === "string" && value.startsWith("data:image");

// pre/posle are full-row snapshots (not diffs), so most fields are identical
// between them on any given change. Only surface the ones that actually
// differ, skipping noise fields — this is what turns a raw snapshot pair
// into a readable "what changed" list.
export const diffFields = (pre, posle) => {
  if (!pre || !posle) return [];
  const keys = new Set([...Object.keys(pre), ...Object.keys(posle)]);
  const rows = [];

  keys.forEach((key) => {
    if (NOISE_FIELDS.has(key)) return;
    const before = pre[key];
    const after = posle[key];
    if (JSON.stringify(before) === JSON.stringify(after)) return;

    // JSON columns (e.g. dizajnOpcije): list each changed key on its own
    // line instead of printing "[object Object] → [object Object]".
    if (isPlainObject(before) || isPlainObject(after)) {
      const b = isPlainObject(before) ? before : {};
      const a = isPlainObject(after) ? after : {};
      new Set([...Object.keys(b), ...Object.keys(a)]).forEach((sub) => {
        if (JSON.stringify(b[sub]) === JSON.stringify(a[sub])) return;
        rows.push({ field: `${key}.${sub}`, before: b[sub], after: a[sub], isImage: false });
      });
      return;
    }

    rows.push({ field: key, before, after, isImage: isDataUri(before) || isDataUri(after) });
  });

  return rows;
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// gs.be now stores design PDFs in history as "[PDF <bytes> B #<hash>]";
// entries logged before that hold the whole PDF as base64 ("JVBER" = "%PDF").
const PDF_SUMMARY = /^\[PDF (\d+) B #([0-9a-f]+)\]$/;

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// How to show one before/after value: { text } for plain display, plus
// { pdfBase64 } when the full PDF is available to open.
export const describeValue = (value) => {
  if (value === null || value === undefined || value === "") return { text: "—" };
  if (typeof value === "string") {
    const summary = value.match(PDF_SUMMARY);
    if (summary) return { text: `PDF · ${formatBytes(Number(summary[1]))}` };
    const base64 = value.replace(/^data:application\/pdf;base64,/, "");
    if (base64.startsWith("JVBER")) {
      return { text: `PDF · ${formatBytes(Math.floor((base64.length * 3) / 4))}`, pdfBase64: base64 };
    }
    return { text: value };
  }
  if (typeof value === "object") return { text: JSON.stringify(value) };
  return { text: String(value) };
};

export const openPdf = (base64) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export { isDataUri };
